import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  api,
  ErrorDeApi,
  type BancoSat,
  type Cliente,
  type CuentaBancaria,
  type CuentaDeCatalogo,
  type ImportacionBancaria,
  type MovimientoBancario,
  type PerfilBancario,
  type RenglonDeEstadoDeCuenta,
  type ResultadoDeEstadoDeCuenta,
  type RolDeUsuario,
} from '../servicios/api'
import { fechaContable } from '../fechas'
import { PerfilesBancarios } from '../componentes/PerfilesBancarios'
import { EmparejamientoBancario } from '../componentes/EmparejamientoBancario'
import { ConciliacionDelPeriodo } from '../componentes/ConciliacionDelPeriodo'
import { PartidasEnConciliacion } from '../componentes/PartidasEnConciliacion'

const importe = (n: number) => n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

/** Los instantes del API vienen en UTC: se muestran en hora local (ver fechas.ts). */
const instante = (iso: string) => new Date(iso).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })

/** El mismo tope que aplica el servidor: avisarlo antes de subir ahorra la espera. */
export const TAMANO_MAXIMO_DE_ARCHIVO = 5 * 1024 * 1024

const SIN_PERMISO = 'Tu rol no puede hacer este cambio: pide a un contador o a un administrador del despacho que lo haga.'

const mensaje = (e: unknown, respaldo: string) =>
  e instanceof ErrorDeApi ? (e.estado === 403 ? SIN_PERMISO : e.message) : respaldo

const descripcion = (c: CuentaBancaria) => `${c.banco} ···${c.ultimosCuatroDigitos}`

/**
 * Conciliación bancaria del cliente (RF-1300 a RF-1305, RF-1307): sus cuentas de cheques, los
 * perfiles con que se leen los estados de cuenta del banco, la importación con vista previa y el
 * emparejamiento de los movimientos contra pólizas o pagos de CFDI.
 * Se llama «conciliación bancaria» siempre y completo: «conciliación» sola ya es, en el producto,
 * el cruce de CFDI contra pólizas.
 */
export function ConciliacionBancaria() {
  const { id: clienteId } = useParams<{ id: string }>()
  const [cliente, setCliente] = useState<Cliente>()
  // undefined: todavía no se sabe; null: no se pudo saber, y entonces decide el servidor.
  const [rol, setRol] = useState<RolDeUsuario | null>()
  const [cuentas, setCuentas] = useState<CuentaBancaria[]>()
  const [perfiles, setPerfiles] = useState<PerfilBancario[]>()
  const [seleccionada, setSeleccionada] = useState<string>()
  const [errorCarga, setErrorCarga] = useState('')
  const [errorAccion, setErrorAccion] = useState('')
  const [ocupado, setOcupado] = useState(false)
  const [registrando, setRegistrando] = useState(false)
  const [viendoPerfiles, setViendoPerfiles] = useState(false)
  // Cerrar o reabrir la conciliación de un mes bloquea o desbloquea su emparejamiento (RN-38):
  // las secciones que dependen de eso se vuelven a montar para recargarse.
  const [version, setVersion] = useState(0)

  const conocido = rol !== undefined
  // Las guardas de la SPA son presentación (CLAUDE.md): el servidor vuelve a revisar cada cambio.
  const puedeAdministrar = conocido && (rol === null || rol === 'Administrador' || rol === 'Contador')
  const puedeImportar = conocido && rol !== 'SoloLectura'
  // RN-38: reabrir una conciliación cerrada es solo del administrador.
  const esAdministrador = conocido && (rol === null || rol === 'Administrador')

  async function cargarCuentas() {
    if (!clienteId) return
    try {
      const lista = await api.cuentasBancarias(clienteId)
      setCuentas(lista)
      setSeleccionada((actual) => actual ?? lista[0]?.id)
    } catch (e) {
      setCuentas(undefined)
      setErrorCarga(mensaje(e, 'No se pudieron cargar las cuentas bancarias del cliente.'))
    }
  }

  async function cargarPerfiles() {
    try {
      setPerfiles(await api.perfilesBancarios())
    } catch (e) {
      setPerfiles(undefined)
      setErrorCarga(mensaje(e, 'No se pudieron cargar los perfiles de importación del despacho.'))
    }
  }

  useEffect(() => {
    if (!clienteId) return
    api.obtenerCliente(clienteId).then(setCliente).catch(() => setCliente(undefined))
    api.despachoActual().then((d) => setRol(d.rolActual)).catch(() => setRol(null))
    void cargarCuentas()
    void cargarPerfiles()
  }, [clienteId])

  async function cambiarEstado(c: CuentaBancaria) {
    if (!clienteId) return
    setOcupado(true)
    setErrorAccion('')
    try {
      if (c.estado === 'Activa') await api.desactivarCuentaBancaria(clienteId, c.id)
      else await api.reactivarCuentaBancaria(clienteId, c.id)
      await cargarCuentas()
    } catch (e) {
      setErrorAccion(mensaje(e, `No se pudo ${c.estado === 'Activa' ? 'desactivar' : 'reactivar'} la cuenta.`))
    } finally {
      setOcupado(false)
    }
  }

  if (!clienteId) return <p>Cargando…</p>

  const cuenta = cuentas?.find((c) => c.id === seleccionada)

  return (
    <>
      <p className="sutil"><Link to={`/clientes/${clienteId}`}>← {cliente?.razonSocial ?? 'Expediente'}</Link></p>

      <header className="encabezado linea-base">
        <h1>Conciliación bancaria</h1>
        {cliente && <span className="encabezado-sub mono">{cliente.rfc} · {cliente.razonSocial}</span>}
        <span className="encabezado-acciones">
          <button className="secundario" onClick={() => setViendoPerfiles((v) => !v)} aria-expanded={viendoPerfiles}>
            Perfiles de importación
          </button>
        </span>
      </header>

      <p className="nota">
        Registra las cuentas de cheques del cliente e importa sus estados de cuenta en CSV o XLSX. De cada cuenta solo se
        guardan los últimos cuatro dígitos: ni el número completo, ni la CLABE, ni contraseñas de banca en línea.
      </p>

      {errorCarga && <p className="error" role="alert">{errorCarga}</p>}
      {errorAccion && <p className="error" role="alert">{errorAccion}</p>}

      {viendoPerfiles && <PerfilesBancarios perfiles={perfiles} puedeEditar={puedeAdministrar} alGuardar={cargarPerfiles} />}

      <section aria-labelledby="titulo-cuentas">
        <header className="encabezado">
          <h2 id="titulo-cuentas">Cuentas bancarias</h2>
          {puedeAdministrar && !registrando && (
            <span className="encabezado-acciones">
              <button className="primario" onClick={() => setRegistrando(true)}>Registrar cuenta bancaria</button>
            </span>
          )}
        </header>

        {cuentas && cuentas.length === 0 && (
          <p className="vacio">
            Este cliente aún no tiene cuentas bancarias.
            {puedeAdministrar
              ? ' Registra la primera: necesitas su cuenta contable de registro, por ejemplo una subcuenta de Bancos.'
              : ' Pide a un contador o a un administrador que registre la primera.'}
          </p>
        )}

        {cuentas && cuentas.length > 0 && (
          <div className="tabla-envoltura">
            <table className="tabla">
              <thead>
                <tr>
                  <th scope="col">Cuenta</th>
                  <th scope="col">Cuenta contable</th>
                  <th scope="col" className="num">Saldo inicial</th>
                  <th scope="col" className="num">Movimientos</th>
                  <th scope="col">Estado</th>
                  <th scope="col">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {cuentas.map((c) => (
                  <tr key={c.id} aria-current={c.id === seleccionada ? 'true' : undefined}>
                    <th scope="row">
                      {descripcion(c)}
                      <div className="sutil" style={{ fontSize: 11 }}>{c.moneda}{c.alias ? ` · ${c.alias}` : ''}</div>
                    </th>
                    <td>{c.cuentaContable}</td>
                    <td className="num">
                      <span className="mono">{importe(c.saldoInicial)}</span>
                      <div className="sutil" style={{ fontSize: 11 }}>al inicio del {fechaContable(c.fechaDelSaldoInicial)}</div>
                    </td>
                    <td className="num">
                      {c.movimientos}
                      {c.ultimoMovimiento && (
                        <div className="sutil" style={{ fontSize: 11 }}>último {fechaContable(c.ultimoMovimiento)}</div>
                      )}
                    </td>
                    <td>{c.estado}</td>
                    <td>
                      <span className="acciones-de-fila">
                        {c.id !== seleccionada && (
                          <button className="enlace" onClick={() => setSeleccionada(c.id)} aria-label={`Ver ${descripcion(c)}`}>
                            Ver
                          </button>
                        )}
                        {puedeAdministrar && (
                          <button className="enlace" disabled={ocupado} onClick={() => void cambiarEstado(c)}>
                            {c.estado === 'Activa' ? 'Desactivar' : 'Reactivar'}
                          </button>
                        )}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {registrando && (
          <AltaDeCuenta
            clienteId={clienteId}
            alCancelar={() => setRegistrando(false)}
            alRegistrar={async (nueva) => {
              setRegistrando(false)
              setSeleccionada(nueva.id)
              await cargarCuentas()
            }}
          />
        )}
      </section>

      {cuenta && (
        <EstadoDeCuenta
          key={cuenta.id}
          clienteId={clienteId}
          cuenta={cuenta}
          perfiles={perfiles}
          puedeImportar={puedeImportar}
          puedeCrearPerfiles={puedeAdministrar}
          alAbrirPerfiles={() => setViendoPerfiles(true)}
          alImportar={cargarCuentas}
        />
      )}

      {/* RF-1304, RF-1305, RF-1307: el emparejamiento lo hacen el administrador y el contador; el
          auxiliar y solo lectura lo ven. */}
      {cuenta && (
        <EmparejamientoBancario key={`emparejamiento-${cuenta.id}-${version}`} clienteId={clienteId} cuenta={cuenta} puedeEmparejar={puedeAdministrar} />
      )}

      {/* RF-1308: el documento del mes, que cierran el administrador y el contador (RN-39) y solo el
          administrador reabre (RN-38). RF-1309: lo que sigue sin emparejar, por antigüedad. */}
      {cuenta && (
        <ConciliacionDelPeriodo key={`conciliacion-${cuenta.id}`} clienteId={clienteId} cuenta={cuenta}
          puedeCerrar={puedeAdministrar} puedeReabrir={esAdministrador} alCambiar={() => setVersion((v) => v + 1)} />
      )}
      {cuenta && <PartidasEnConciliacion key={`partidas-${cuenta.id}-${version}`} clienteId={clienteId} cuenta={cuenta} />}
    </>
  )
}

function AltaDeCuenta({
  clienteId,
  alCancelar,
  alRegistrar,
}: {
  clienteId: string
  alCancelar: () => void
  alRegistrar: (cuenta: CuentaBancaria) => Promise<void>
}) {
  const [bancos, setBancos] = useState<BancoSat[]>()
  const [cuentasDeRegistro, setCuentasDeRegistro] = useState<CuentaDeCatalogo[]>()
  const [error, setError] = useState('')
  const [ocupado, setOcupado] = useState(false)
  const [claveBanco, setClaveBanco] = useState('')
  const [cuentaContableId, setCuentaContableId] = useState('')
  const [moneda, setMoneda] = useState('MXN')
  const [digitos, setDigitos] = useState('')
  const [alias, setAlias] = useState('')
  const [saldo, setSaldo] = useState('')
  const [fecha, setFecha] = useState('')

  useEffect(() => {
    api.bancosSat().then(setBancos).catch((e) => setError(mensaje(e, 'No se pudo cargar el catálogo de bancos del SAT.')))
    api.catalogoDeCuentas(clienteId)
      // Solo las de registro y activas aceptan movimientos (RN-05): una acumulativa no puede ser la cuenta del banco.
      .then((c) => setCuentasDeRegistro(c.filter((x) => x.esDeRegistro && x.estado === 'Activa')))
      .catch((e) => setError(mensaje(e, 'No se pudo cargar el catálogo de cuentas del cliente.')))
  }, [clienteId])

  async function registrar() {
    setOcupado(true)
    setError('')
    try {
      const nueva = await api.registrarCuentaBancaria(clienteId, {
        cuentaContableId,
        claveBanco,
        moneda: moneda.trim().toUpperCase(),
        ultimosCuatroDigitos: digitos.trim(),
        ...(alias.trim() && { alias: alias.trim() }),
        saldoInicial: Number(saldo),
        fechaDelSaldoInicial: fecha,
      })
      await alRegistrar(nueva)
    } catch (e) {
      setError(mensaje(e, 'No se pudo registrar la cuenta bancaria.'))
    } finally {
      setOcupado(false)
    }
  }

  return (
    <form
      className="tarjeta"
      aria-label="Registrar cuenta bancaria"
      onSubmit={(e) => {
        e.preventDefault()
        void registrar()
      }}
    >
      <h3>Registrar cuenta bancaria</h3>

      {cuentasDeRegistro && cuentasDeRegistro.length === 0 && (
        <p className="aviso">
          El catálogo del cliente no tiene cuentas de registro activas. Cárgalo primero en{' '}
          <Link to={`/clientes/${clienteId}/catalogo`}>Catálogo de cuentas</Link>.
        </p>
      )}

      <div className="filtros">
        <label>
          Banco
          <select value={claveBanco} onChange={(e) => setClaveBanco(e.target.value)} required>
            <option value="">Elige el banco</option>
            {bancos?.map((b) => <option key={b.clave} value={b.clave}>{b.nombre}</option>)}
          </select>
        </label>
        <label>
          Cuenta contable de registro
          <select value={cuentaContableId} onChange={(e) => setCuentaContableId(e.target.value)} required>
            <option value="">Elige la cuenta</option>
            {cuentasDeRegistro?.map((c) => <option key={c.id} value={c.id}>{c.numero} {c.nombre}</option>)}
          </select>
        </label>
        <label>
          Moneda
          <input value={moneda} onChange={(e) => setMoneda(e.target.value)} maxLength={3} required className="mono" />
        </label>
        <label>
          Últimos cuatro dígitos
          <input value={digitos} onChange={(e) => setDigitos(e.target.value)} inputMode="numeric" pattern="\d{4}" maxLength={4}
            required className="mono" placeholder="1234" />
        </label>
        <label>
          Alias (opcional)
          <input value={alias} onChange={(e) => setAlias(e.target.value)} maxLength={100} placeholder="Cuenta de nómina" />
        </label>
        <label>
          Saldo al inicio del día
          <input type="number" step="0.01" value={saldo} onChange={(e) => setSaldo(e.target.value)} required />
        </label>
        <label>
          Fecha del saldo
          <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} required />
        </label>
      </div>

      <p className="nota">
        Captura solo los últimos cuatro dígitos: ContaQuick nunca guarda el número completo de la cuenta ni la CLABE. Los
        movimientos anteriores a la fecha del saldo ya están en él y no se importan.
      </p>

      {error && <p className="error" role="alert">{error}</p>}

      <div className="acciones">
        <button type="button" onClick={alCancelar}>Cancelar</button>
        <button type="submit" className="primario" disabled={ocupado}>{ocupado ? 'Registrando…' : 'Registrar cuenta'}</button>
      </div>
    </form>
  )
}

function EstadoDeCuenta({
  clienteId,
  cuenta,
  perfiles,
  puedeImportar,
  puedeCrearPerfiles,
  alAbrirPerfiles,
  alImportar,
}: {
  clienteId: string
  cuenta: CuentaBancaria
  perfiles: PerfilBancario[] | undefined
  puedeImportar: boolean
  puedeCrearPerfiles: boolean
  alAbrirPerfiles: () => void
  alImportar: () => Promise<void>
}) {
  const [perfilId, setPerfilId] = useState('')
  const [archivo, setArchivo] = useState<File | null>(null)
  const [vista, setVista] = useState<ResultadoDeEstadoDeCuenta>()
  const [resultado, setResultado] = useState<ResultadoDeEstadoDeCuenta>()
  const [importaciones, setImportaciones] = useState<ImportacionBancaria[]>()
  const [movimientos, setMovimientos] = useState<MovimientoBancario[]>()
  const [desde, setDesde] = useState('')
  const [hasta, setHasta] = useState('')
  const [error, setError] = useState('')
  const [ocupado, setOcupado] = useState(false)

  async function cargarMovimientos(filtros: { desde?: string; hasta?: string } = { desde, hasta }) {
    try {
      setMovimientos(await api.movimientosBancarios(clienteId, cuenta.id, {
        ...(filtros.desde && { desde: filtros.desde }),
        ...(filtros.hasta && { hasta: filtros.hasta }),
      }))
    } catch (e) {
      setError(mensaje(e, 'No se pudieron cargar los movimientos de la cuenta.'))
    }
  }

  async function cargarHistorial() {
    try {
      setImportaciones(await api.importacionesBancarias(clienteId, cuenta.id))
    } catch (e) {
      setError(mensaje(e, 'No se pudo cargar el historial de importaciones.'))
    }
  }

  useEffect(() => {
    void cargarHistorial()
    void cargarMovimientos({})
  }, [clienteId, cuenta.id])

  // Cambiar el perfil o el archivo invalida la vista previa: lo que se incorpora tiene que ser
  // exactamente lo que se previsualizó.
  function elegirPerfil(id: string) {
    setPerfilId(id)
    setVista(undefined)
  }

  function elegirArchivo(nuevo: File | null) {
    setArchivo(nuevo)
    setVista(undefined)
    setError(nuevo && nuevo.size > TAMANO_MAXIMO_DE_ARCHIVO
      ? 'El archivo pesa más de 5 MB: exporta un periodo más corto desde la banca en línea.'
      : '')
  }

  async function previsualizar() {
    if (!archivo || !perfilId) return
    setOcupado(true)
    setError('')
    setResultado(undefined)
    try {
      setVista(await api.vistaPreviaDeEstadoDeCuenta(clienteId, cuenta.id, perfilId, archivo))
    } catch (e) {
      setError(mensaje(e, 'No se pudo leer el estado de cuenta.'))
    } finally {
      setOcupado(false)
    }
  }

  async function incorporar() {
    if (!archivo || !perfilId) return
    setOcupado(true)
    setError('')
    try {
      setResultado(await api.importarEstadoDeCuenta(clienteId, cuenta.id, perfilId, archivo))
      setVista(undefined)
      setArchivo(null)
      await Promise.all([cargarHistorial(), cargarMovimientos(), alImportar()])
    } catch (e) {
      setError(mensaje(e, 'No se pudo incorporar el estado de cuenta.'))
    } finally {
      setOcupado(false)
    }
  }

  const demasiadoGrande = archivo !== null && archivo.size > TAMANO_MAXIMO_DE_ARCHIVO

  return (
    <section aria-labelledby="titulo-estado-de-cuenta">
      <h2 id="titulo-estado-de-cuenta">Estado de cuenta de {descripcion(cuenta)}</h2>

      {error && <p className="error" role="alert">{error}</p>}

      {puedeImportar && cuenta.estado === 'Desactivada' && (
        <p className="aviso">Esta cuenta está desactivada y no admite estados de cuenta. Reactívala si sigue en uso.</p>
      )}

      {puedeImportar && cuenta.estado === 'Activa' && perfiles && perfiles.length === 0 && (
        <p className="aviso">
          Para importar hace falta un perfil que diga cómo leer el archivo del banco, y el despacho aún no tiene ninguno.{' '}
          {puedeCrearPerfiles
            ? <button className="enlace" onClick={alAbrirPerfiles}>Crear un perfil</button>
            : 'Pide a un contador o a un administrador que cree el del banco.'}
        </p>
      )}

      {puedeImportar && cuenta.estado === 'Activa' && perfiles && perfiles.length > 0 && (
        <form
          className="filtros"
          aria-label="Importar estado de cuenta"
          onSubmit={(e) => {
            e.preventDefault()
            void previsualizar()
          }}
        >
          <label>
            Perfil
            <select value={perfilId} onChange={(e) => elegirPerfil(e.target.value)} required>
              <option value="">Elige el perfil del banco</option>
              {perfiles.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
            </select>
          </label>
          <label>
            Archivo (CSV o XLSX)
            <input
              type="file"
              accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              // Sin `required`: el botón ya se apaga mientras no haya archivo, y la validación nativa de
              // un input file no sobrevive a jsdom (las pruebas no podrían enviar el formulario).
              onChange={(e) => elegirArchivo(e.target.files?.[0] ?? null)}
            />
          </label>
          <button type="submit" className="secundario" disabled={ocupado || !archivo || !perfilId || demasiadoGrande}>
            {ocupado && !vista ? 'Leyendo…' : 'Ver vista previa'}
          </button>
        </form>
      )}

      {vista && (
        <div className="tarjeta" aria-label="Vista previa">
          <h3>Vista previa · todavía no se guardó nada</h3>
          <ResumenDeEstadoDeCuenta r={vista} incorporado={false} />
          <div className="acciones">
            <button className="primario" disabled={ocupado || vista.nuevos === 0} onClick={() => void incorporar()}>
              {ocupado ? 'Incorporando…' : `Incorporar ${vista.nuevos} ${vista.nuevos === 1 ? 'movimiento' : 'movimientos'}`}
            </button>
          </div>
        </div>
      )}

      {resultado && (
        <div className="tarjeta" role="status" aria-label="Resultado de la importación">
          <h3>Estado de cuenta incorporado</h3>
          <ResumenDeEstadoDeCuenta r={resultado} incorporado />
        </div>
      )}

      <h3>Movimientos</h3>
      <form
        className="filtros"
        aria-label="Filtrar movimientos"
        onSubmit={(e) => {
          e.preventDefault()
          void cargarMovimientos()
        }}
      >
        <label>
          Desde
          <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} />
        </label>
        <label>
          Hasta
          <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} />
        </label>
        <button type="submit" className="secundario">Filtrar</button>
      </form>

      {movimientos && movimientos.length === 0 && (
        <p className="vacio">
          {desde || hasta
            ? 'No hay movimientos en ese rango de fechas.'
            : 'Aún no se ha importado ningún estado de cuenta de esta cuenta.'}
        </p>
      )}
      {movimientos && movimientos.length > 0 && (
        <div className="tabla-envoltura">
          <table className="tabla">
            <thead>
              <tr>
                <th scope="col">Fecha</th>
                <th scope="col">Concepto</th>
                <th scope="col">Referencia</th>
                <th scope="col" className="num">Cargo</th>
                <th scope="col" className="num">Abono</th>
                <th scope="col" className="num">Saldo</th>
                <th scope="col">Conciliación bancaria</th>
              </tr>
            </thead>
            <tbody>
              {movimientos.map((m) => (
                <tr key={m.id}>
                  <td>{fechaContable(m.fecha)}</td>
                  <td>{m.concepto}</td>
                  <td className="mono">{m.referencia ?? <span className="sutil">—</span>}</td>
                  <td className="num mono">{m.cargo ? importe(m.cargo) : ''}</td>
                  <td className="num mono">{m.abono ? importe(m.abono) : ''}</td>
                  <td className="num mono">{m.saldo === null ? <span className="sutil">—</span> : importe(m.saldo)}</td>
                  <td>{m.estadoDeConciliacion === 'Conciliado' ? 'Conciliado' : 'Pendiente'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h3>Importaciones</h3>
      {importaciones && importaciones.length === 0 && (
        <p className="vacio">Esta cuenta aún no tiene estados de cuenta importados.</p>
      )}
      {importaciones && importaciones.length > 0 && (
        <div className="tabla-envoltura">
          <table className="tabla">
            <thead>
              <tr>
                <th scope="col">Importado</th>
                <th scope="col">Archivo</th>
                <th scope="col">Perfil</th>
                <th scope="col" className="num">Renglones</th>
                <th scope="col" className="num">Incorporados</th>
                <th scope="col" className="num">Ya estaban</th>
                <th scope="col" className="num">Rechazados</th>
              </tr>
            </thead>
            <tbody>
              {importaciones.map((i) => (
                <tr key={i.id}>
                  <td>{instante(i.importadaEnUtc)}</td>
                  <td>{i.nombreDeArchivo}</td>
                  <td>{i.perfil}</td>
                  <td className="num">{i.renglones}</td>
                  <td className="num">{i.incorporados}</td>
                  <td className="num">{i.duplicados}</td>
                  <td className="num">{i.rechazados}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}

/** Los tres conteos, cada cero con su explicación, y las muestras de lo que hay detrás. */
function ResumenDeEstadoDeCuenta({ r, incorporado }: { r: ResultadoDeEstadoDeCuenta; incorporado: boolean }) {
  const nuevos = r.nuevos === 0
    ? (incorporado
      ? 'No se incorporó ningún movimiento: todo lo del archivo ya estaba en la cuenta o se rechazó.'
      : 'Ningún renglón es nuevo: todo lo del archivo ya está en la cuenta o se rechazó.')
    : `${r.nuevos} ${incorporado ? (r.nuevos === 1 ? 'incorporado' : 'incorporados') : (r.nuevos === 1 ? 'nuevo, entraría' : 'nuevos, entrarían')} a la cuenta.`
  const duplicados = r.duplicados === 0
    ? 'Ninguno estaba ya en la cuenta.'
    : `${r.duplicados} ya ${r.duplicados === 1 ? 'estaba' : 'estaban'} en la cuenta y se ${r.duplicados === 1 ? 'omite' : 'omiten'}: reimportar un periodo no duplica movimientos.`
  const rechazados = r.rechazados === 0
    ? 'Ningún renglón se rechazó.'
    : `${r.rechazados} ${r.rechazados === 1 ? 'rechazado' : 'rechazados'}; abajo cada uno con su renglón y su motivo.`

  return (
    <>
      <p>
        {r.renglonesDeDatos === 0
          ? 'El archivo no trae renglones de datos debajo del encabezado.'
          : `El archivo trae ${r.renglonesDeDatos} ${r.renglonesDeDatos === 1 ? 'renglón' : 'renglones'} de datos.`}
      </p>
      <ul className="lista-compacta">
        <li>{nuevos}</li>
        <li>{duplicados}</li>
        <li>{rechazados}</li>
      </ul>

      <TablaDeRenglones titulo={incorporado ? 'Incorporados' : 'Nuevos'} renglones={r.muestraDeNuevos} total={r.nuevos} />
      <TablaDeRenglones titulo="Ya estaban en la cuenta" renglones={r.muestraDeDuplicados} total={r.duplicados} />

      {r.muestraDeRechazados.length > 0 && (
        <>
          <h4>Rechazados</h4>
          <Truncado mostrados={r.muestraDeRechazados.length} total={r.rechazados} />
          <ul className="lista-compacta">
            {r.muestraDeRechazados.map((x) => (
              <li key={x.renglon}><span className="mono">Renglón {x.renglon}</span> · {x.motivo}</li>
            ))}
          </ul>
        </>
      )}
    </>
  )
}

function Truncado({ mostrados, total }: { mostrados: number; total: number }) {
  return mostrados < total ? <p className="sutil">Se muestran los primeros {mostrados} de {total}.</p> : null
}

function TablaDeRenglones({ titulo, renglones, total }: { titulo: string; renglones: RenglonDeEstadoDeCuenta[]; total: number }) {
  if (renglones.length === 0) return null
  return (
    <>
      <h4>{titulo}</h4>
      <Truncado mostrados={renglones.length} total={total} />
      <div className="tabla-envoltura">
        <table className="tabla" aria-label={titulo}>
          <thead>
            <tr>
              <th scope="col" className="num">Renglón</th>
              <th scope="col">Fecha</th>
              <th scope="col">Concepto</th>
              <th scope="col">Referencia</th>
              <th scope="col" className="num">Cargo</th>
              <th scope="col" className="num">Abono</th>
            </tr>
          </thead>
          <tbody>
            {renglones.map((x) => (
              <tr key={x.renglon}>
                <td className="num mono">{x.renglon}</td>
                <td>{fechaContable(x.fecha)}</td>
                <td>{x.concepto}</td>
                <td className="mono">{x.referencia ?? ''}</td>
                <td className="num mono">{x.cargo ? importe(x.cargo) : ''}</td>
                <td className="num mono">{x.abono ? importe(x.abono) : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}
